import {useSession} from 'next-auth/react'
import {useQuery, useQueryClient} from 'react-query'

import {getClientGQL} from '@/utils/clientGql'
import {BrandFragment} from '@/utils/graphqlSdk'

const BRANDS_STALE_TIME = 5 * 60 * 1000

export const sortBrands = (brands: BrandFragment[] | undefined): BrandFragment[] =>
  brands ? brands.toSorted((a, b) => a.name.localeCompare(b.name)) : []

export const useBrand = (id: string | undefined | null) =>
  useQuery(
    ['brand', id],
    async () => {
      try {
        const gqlclient = await getClientGQL()
        return (await gqlclient.brandGetByIdForUser({id: id!, deleted: false}))?.brand?.[0]
      } catch (e) {
        throw new Error(e)
      }
    },
    {
      enabled: !!id,
    },
  )

export const useUserMonitoredBrandsTotalCount = (userId: string) =>
  useQuery(
    ['userMonitoredBrandsTotalCount', userId],
    async () => {
      try {
        const gqlclient = await getClientGQL()

        const data = await gqlclient.monitoredBrandsTotalCount({userId})

        const totalCount = data.brand_aggregate.aggregate?.count ?? 0
        return {totalCount}
      } catch (e) {
        throw new Error(e)
      }
    },
    {
      enabled: Boolean(userId),
    },
  )

export const useBrands = () => {
  const {data: session} = useSession()

  return useQuery(
    'brands',
    async () => {
      try {
        const gqlclient = await getClientGQL()
        return (
          await gqlclient.brandGetManyForUser({
            filter: {deleted: {_eq: false}},
          })
        ).brand
      } catch (e) {
        throw new Error(e)
      }
    },
    {
      enabled: Boolean(session),
      staleTime: BRANDS_STALE_TIME,
    },
  )
}

export const useAreItemsUnassigned = () => {
  const {data: session} = useSession()

  return useQuery(
    'brands_aggregate',
    async () => {
      try {
        const gqlclient = await getClientGQL()
        const {
          registration_request_aggregate: {aggregate: registrationRequests},
          trademark_aggregate: {aggregate: trademarks},
          trademark_request_aggregate: {aggregate: trademarkRequests},
          service_request_aggregate: {aggregate: serviceRequests},
        } = await gqlclient.unassignedOrders()

        return Boolean(
          registrationRequests?.count ||
            trademarks?.count ||
            trademarkRequests?.count ||
            serviceRequests?.count,
        )
      } catch (e) {
        throw new Error(e)
      }
    },
    {
      enabled: Boolean(session),
      staleTime: BRANDS_STALE_TIME,
    },
  )
}

export const useInvalidateBrandQueries = () => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries('brands')
    queryClient.invalidateQueries('brands_aggregate')
  }
}
